import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SuccessStories = ({ onClose }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const stories = [
    { id: 1, name: 'Kasun Perera', achievement: 'Island Rank 02', batch: '2024 Biology Stream', img: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=600' },
    { id: 2, name: 'Amali Silva', achievement: '9A\'s - O/L Distinction', batch: '2023 Secondary', img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=600' },
    { id: 3, name: 'Nimal Bandara', achievement: 'Engineering Entrance', batch: '2024 Maths Stream', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=600' },
    { id: 4, name: 'Sajini Perera', achievement: 'Island Rank 15', batch: '2022 Commerce Stream', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=600' },
    { id: 5, name: 'Nuwan Thilaka', achievement: 'District Rank 01', batch: '2024 Maths Stream', img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=600' },
    { id: 6, name: 'Ishara Madu', achievement: 'Medical Faculty', batch: '2023 Biology Stream', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=600' },
  ];

  const SkeletonCard = () => (
    <div className="bg-gray-100 rounded-[2.5rem] aspect-[4/5] animate-pulse"></div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 lg:p-12">
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-white/95 backdrop-blur-2xl"
      />

      <motion.div 
        initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
        className="relative bg-white w-full max-w-7xl max-h-[90vh] overflow-y-auto rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] no-scrollbar border border-gray-100"
      >
        {/* Header - More Creative Text */}
        <div className="sticky top-0 z-20 bg-white/60 backdrop-blur-xl p-8 md:p-12 flex justify-between items-start">
          <div>
            <span className="text-red-600 font-black text-[10px] uppercase tracking-[0.4em] block mb-2">Our Proud Legacy</span>
            <h2 className="text-3xl md:text-5xl font-black text-indigo-950 uppercase tracking-tighter leading-none">
              Voices of <span className="text-red-600">Victory</span>
            </h2>
            <p className="text-gray-400 mt-4 font-medium max-w-md text-sm md:text-base">
              The real-life success stories of students who turned their dreams into reality at Vidulaka.
            </p>
          </div>
          <button onClick={onClose} className="w-14 h-14 rounded-2xl hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all flex items-center justify-center border border-gray-100 group">
            <span className="text-2xl group-hover:rotate-90 transition-transform duration-300">✕</span>
          </button>
        </div>

        {/* Gallery Grid */}
        <div className="p-8 md:p-12 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="wait">
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
              ) : (
                stories.map((story) => (
                  <motion.div 
                    layout key={story.id}
                    initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="group relative aspect-[4/5] rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-700"
                  >
                    <img 
                      src={story.img} 
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                      alt={story.name} 
                    />

                    {/* Overlay Details */}
                    <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-10 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        <div className="inline-block px-3 py-1 bg-red-600 text-white text-[9px] font-black uppercase tracking-widest rounded-full mb-3 shadow-lg">
                          {story.achievement}
                        </div>
                        <h3 className="text-3xl font-black text-white leading-tight">{story.name}</h3>
                        <p className="text-white/60 text-xs mt-2 font-bold uppercase tracking-[0.2em]">{story.batch}</p>
                      </div>
                    </div>

                    {/* Achievement Icon */}
                    <div className="absolute top-8 left-8 w-10 h-10 bg-white/20 backdrop-blur-md border border-white/30 rounded-full flex items-center justify-center text-white opacity-100 group-hover:bg-red-600 group-hover:border-red-600 transition-all duration-300">
                       <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                       </svg>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="pb-16 text-center">
            <button onClick={onClose} className="bg-slate-50 text-slate-400 px-8 py-3 rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-red-50 hover:text-red-600 transition-all border border-gray-100">
              Back to Home
            </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SuccessStories;